import axios from 'axios';
import logger from '../utils/logger';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Log API errors and handle 401 globally
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const url = err.config?.url;
    const method = err.config?.method?.toUpperCase();
    const detail = err.response?.data?.detail;

    if (status === 401) {
      logger.warn('api', 'Session expired, redirecting to login', { url, method });
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else if (status >= 500) {
      logger.error('api', 'Server error', { url, method, status, detail });
    } else if (status >= 400) {
      logger.warn('api', 'Request failed', { url, method, status, detail });
    } else {
      logger.error('api', 'Network error', { url, method, message: err.message });
    }

    return Promise.reject(err);
  }
);

export default api;

// ─── Auth ──────────────────────────────────────────────────────────────────
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
};

// ─── Leads ─────────────────────────────────────────────────────────────────
export const leadsApi = {
  list:     (params) => api.get('/leads', { params }),
  get:      (id)     => api.get(`/leads/${id}`),
  create:   (data)   => api.post('/leads', data),
  update:   (id, data) => api.patch(`/leads/${id}`, data),
  delete:   (id)     => api.delete(`/leads/${id}`),
  assign:   (id, agentId) => api.patch(`/leads/${id}/assign`, { agent_id: agentId }),
  activity: (id)     => api.get(`/leads/${id}/activity`),
  addNote:  (id, text) => api.post(`/leads/${id}/note`, { text }),
  excelTemplate: () => api.get('/leads/excel-template', { responseType: 'blob' }),
  excelPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/leads/excel-preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  excelConfirm: (rows) => api.post('/leads/excel-confirm', { rows }),
  bulkAssign:      (leadIds, agentId) => api.post('/leads/bulk-assign', { lead_ids: leadIds, agent_id: agentId }),
  bulkChangeStage: (leadIds, stage)   => api.post('/leads/bulk-stage', { lead_ids: leadIds, stage }),
  bulkDelete:      (leadIds)          => api.post('/leads/bulk-delete', { lead_ids: leadIds }),
};

// ─── Sales ────────────────────────────────────────────────────────────────
export const salesApi = {
  dashboard:      (params) => api.get('/sales/dashboard', { params }),
  filterOptions:  ()       => api.get('/sales/filter-options'),
  list:           (params) => api.get('/sales', { params }),
  get:          (id)     => api.get(`/sales/${id}`),
  create:       (data)   => api.post('/sales', data),
  update:       (id, data) => api.patch(`/sales/${id}`, data),
  advanceStage: (id, stage) => api.patch(`/sales/${id}/stage`, { stage }),
  delete:       (id)     => api.delete(`/sales/${id}`),
  activity:     (id)     => api.get(`/sales/${id}/activity`),
  addNote:      (id, text) => api.post(`/sales/${id}/note`, { text }),
};

// ─── Dashboard ─────────────────────────────────────────────────────────────
export const dashboardApi = {
  stats:         () => api.get('/dashboard/stats'),
  platformStats: () => api.get('/dashboard/platform-stats'),
};

// ─── Users ─────────────────────────────────────────────────────────────────
export const usersApi = {
  list:           (params) => api.get('/users', { params }),
  get:            (id)     => api.get(`/users/${id}`),
  create:         (data)   => api.post('/users', data),
  update:         (id, data) => api.patch(`/users/${id}`, data),
  delete:         (id)     => api.delete(`/users/${id}`),
  changePassword: (data)   => api.post('/users/me/change-password', data),
};

// ─── Org Settings (admin) ──────────────────────────────────────────────────
export const orgSettingsApi = {
  get:    () => api.get('/org/settings'),
  update: (data) => api.patch('/org/settings', data),
};

// ─── Tenants (super admin) ─────────────────────────────────────────────────
export const tenantsApi = {
  list:   (params) => api.get('/tenants', { params }),
  get:    (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.patch(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
};

// ─── Tenant API Keys (super admin) ────────────────────────────────────────
export const apiKeysApi = {
  list:   (tenantId) => api.get(`/tenants/${tenantId}/api-keys`),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/api-keys`, data),
  revoke: (tenantId, keyId) => api.delete(`/tenants/${tenantId}/api-keys/${keyId}`),
};

// ─── Tenant Users (super admin) ───────────────────────────────────────────
export const tenantUsersApi = {
  list:   (tenantId) => api.get(`/tenants/${tenantId}/users`),
  get:    (tenantId, userId) => api.get(`/tenants/${tenantId}/users/${userId}`),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/users`, data),
  update: (tenantId, userId, data) => api.patch(`/tenants/${tenantId}/users/${userId}`, data),
  delete: (tenantId, userId) => api.delete(`/tenants/${tenantId}/users/${userId}`),
};
